// The one antd theme factory (spec §7). antd's algorithm derives hover/active
// shades from the tokens, so it needs concrete hex — never the var() strings.
// App.jsx's root ConfigProvider is the ONLY consumer; no page wraps its own.
import { DARK, LIGHT, SCALE } from './claudeTokens';

// The seven palette stops a preset colour reads (Tag uses 1/3/7 in light and
// 1/3/6 in dark; Badge/Progress read 6), mapped onto one status triple.
const ramp = (name, s) => ({
  [`${name}1`]: s.bg, [`${name}2`]: s.bg, [`${name}3`]: s.border, [`${name}4`]: s.border,
  [`${name}5`]: s.text, [`${name}6`]: s.text, [`${name}7`]: s.text,
});

export const claudeAntdTheme = (isDark, antdTheme) => {
  const p = isDark ? DARK : LIGHT;
  const grey = { bg: p.surface, border: p.border, text: p.textSecondary };
  const statusText = {
    colorSuccess: p.status.completed.text, colorWarning: p.status.awaiting.text,
    colorError: p.status.failed.text, colorInfo: p.info.text,
  };
  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: p.accent,
      colorInfo: p.info.text,
      colorError: p.danger,
      colorSuccess: p.status.completed.text,
      colorWarning: p.status.awaiting.text,
      colorTextLightSolid: p.onAccent,
      colorBgBase: p.bg,
      colorBgLayout: p.bg,
      colorBgContainer: p.surfaceElevated,
      colorBgElevated: p.surfaceElevated,
      colorFillAlter: p.surface,
      // Controls get the strong edge; cards, dividers and table rows the hairline.
      colorBorder: p.borderStrong,
      colorBorderSecondary: p.border,
      colorSplit: p.border,
      colorText: p.textPrimary,
      colorTextSecondary: p.textSecondary,
      colorTextTertiary: p.textTertiary,
      colorTextPlaceholder: p.textTertiary,
      // Link text is the accent's text-safe stop — Lapis Lazuli in light (antd 5.29
      // derives colorLink from colorInfo otherwise). colorLink and colorInfo are SEEDS: formatToken drops
      // seed names from the override set, so the dark algorithm re-derives them
      // (#C18C72 / #939EB9). The component pins below (Button, Typography, Alert,
      // Badge) are where a seed name survives — the Radio.colorPrimary pattern.
      colorLink: p.status.active.text, colorLinkHover: p.accentHover, colorLinkActive: p.status.active.text,
      borderRadius: 8,
      borderRadiusLG: 14, // cards, modals, drawers (antd derives 10 from 8)
      borderRadiusSM: 6,
      boxShadowTertiary: p.shadow.card,
      boxShadowSecondary: p.shadow.cardHover, boxShadow: p.shadow.cardHover,
      boxShadowCard: p.shadow.cardHover, // the alias token hoverable Card lifts to
      controlOutline: isDark ? `${p.highlight}47` : `${p.highlight}38`, // focus ring: Celestial at 0.28 / 0.22
      controlOutlineWidth: 3,
      fontFamily: SCALE.fontSans,
      fontSizeHeading1: 34, fontSizeHeading2: 24, fontSizeHeading3: 19,
      // Semantic status surfaces. antd derives colorSuccessBg etc. from the
      // seed with generate(), which turns our mid-tone status text colours
      // into muddy backgrounds in light mode — so the bg/border stops are
      // pinned to the palette's own status triples instead.
      colorSuccessBg: p.status.completed.bg, colorSuccessBgHover: p.status.completed.bg, colorSuccessBorder: p.status.completed.border,
      colorWarningBg: p.status.awaiting.bg, colorWarningBgHover: p.status.awaiting.bg, colorWarningBorder: p.status.awaiting.border,
      colorErrorBg: p.status.failed.bg, colorErrorBgHover: p.status.failed.bg, colorErrorBorder: p.status.failed.border,
      colorInfoBg: p.info.bg, colorInfoBgHover: p.info.bg,
      colorInfoBorder: p.info.border, colorInfoBorderHover: p.info.border,
      colorInfoText: p.info.text, colorInfoTextHover: p.info.text, colorInfoTextActive: p.info.text,
      // Named presets (Tag/Badge color="green" etc.) come from these seeds; the
      // same generate() problem applies, so the 1–7 stops each preset reads
      // are pinned too: blues become the info triple, greens/reds/ambers/volcano
      // the status triples, purples/pinks a neutral grey. Prefer semantic
      // presets (success/warning/error) in new code.
      blue: p.info.text, cyan: p.info.text, geekblue: p.info.text,
      green: p.status.completed.text, lime: p.status.completed.text,
      red: p.status.failed.text,
      orange: p.status.awaiting.text, gold: p.status.awaiting.text, yellow: p.status.awaiting.text,
      volcano: p.status.active.text,
      purple: p.textSecondary, magenta: p.textSecondary, pink: p.textSecondary,
      ...ramp('blue', p.info), ...ramp('cyan', p.info), ...ramp('geekblue', p.info),
      ...ramp('green', p.status.completed), ...ramp('lime', p.status.completed),
      ...ramp('red', p.status.failed),
      ...ramp('orange', p.status.awaiting), ...ramp('gold', p.status.awaiting), ...ramp('yellow', p.status.awaiting),
      ...ramp('volcano', p.status.active),
      ...ramp('purple', grey), ...ramp('magenta', grey), ...ramp('pink', grey),
    },
    // Light controls sit on chrome with the strong edge; dark controls stay on the card surface.
    components: {
      Card: {
        colorBgContainer: p.surfaceElevated, colorBorderSecondary: p.border, headerBg: 'transparent',
        headerFontSize: 17, headerHeight: 60, headerPadding: 24, bodyPadding: 24, extraColor: p.textSecondary,
      },
      Table: {
        headerBg: 'transparent', headerColor: p.textSecondary, headerSplitColor: 'transparent',
        borderColor: p.border, rowHoverBg: isDark ? p.surfaceHover : p.bg,
        cellPaddingBlock: 14, cellPaddingInline: 12,
      },
      Button: {
        fontWeight: 500, defaultBg: isDark ? p.surfaceElevated : p.chrome, defaultBorderColor: p.borderStrong,
        defaultShadow: p.shadow.pill, primaryShadow: p.shadow.primary, dangerShadow: p.shadow.pill,
        colorLink: p.status.active.text, colorLinkHover: p.accentHover, colorLinkActive: p.status.active.text,
      },
      Tabs: {
        itemColor: p.textSecondary, itemSelectedColor: p.textPrimary, itemHoverColor: p.textPrimary,
        inkBarColor: p.highlight, titleFontSize: 14,
      },
      // antd's dark algorithm re-derives the status SEEDS (colorError etc.) and formatToken
      // strips seed names from the override set, so the status TEXT is pinned per component,
      // where the names survive. bg/border come from the pinned colorXxxBg/Border above.
      Alert: { defaultPadding: '10px 14px', ...statusText },
      Badge: { ...statusText }, // status dots
      Typography: { colorLink: p.status.active.text, colorLinkHover: p.accentHover, colorLinkActive: p.status.active.text },
      Tag: { defaultBg: p.surface, defaultColor: p.textSecondary, borderRadiusSM: 999, ...statusText },
      Progress: { defaultColor: p.highlight, remainingColor: isDark ? p.surfaceHover : p.surface, ...statusText }, // a measure, not a message
      Input: {
        colorBgContainer: isDark ? p.surfaceElevated : p.chrome, activeBorderColor: p.highlight,
        activeShadow: `0 0 0 3px ${isDark ? p.highlight + '47' : p.highlight + '38'}`,
      },
      InputNumber: { colorBgContainer: isDark ? p.surfaceElevated : p.chrome },
      DatePicker: { colorBgContainer: isDark ? p.surfaceElevated : p.chrome, activeBorderColor: p.highlight },
      Select: {
        selectorBg: isDark ? p.surfaceElevated : p.chrome, activeBorderColor: p.highlight,
        optionSelectedBg: p.status.active.bg, optionActiveBg: p.bg, optionSelectedFontWeight: 600,
      },
      Radio: { colorPrimary: p.status.active.text, buttonBg: isDark ? p.surfaceElevated : p.chrome },
      Layout: { bodyBg: p.bg, headerBg: p.chrome, siderBg: p.surface },
      Menu: { itemBg: 'transparent', itemSelectedBg: `${p.accent}26`, itemSelectedColor: p.ink }, // 0.15 alpha tint
      Modal: { contentBg: p.surfaceElevated, headerBg: p.surfaceElevated },
      Drawer: { colorBgElevated: p.surfaceElevated },
    },
  };
};

export default claudeAntdTheme;
